import React from "react";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  SaveAlt as SaveAltIcon,
  Share as ShareIcon,
  AccountTree as FlownodeIcon,
} from "@mui/icons-material";
import { Task } from "model/activitiesModel";
import Labels from "components/labels/Labels";
import { getLabelDataFromJsonTags } from "common/utilities";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";
import { getTranslation } from "common";
import { useTranslations } from "hooks";

interface MobileDetailHeaderProps {
  task: Task;
  onClose: () => void;
  onDownload?: () => void;
  onPreview?: () => void;
  onShare?: () => void;
  /** Mirrors TaskDetailDialog: disable action buttons while generating the PDF. */
  disabled?: boolean;
  /** Show the secondary action buttons (Download / Preview / Share). */
  showActions?: boolean;
}

/**
 * Compact header for the mobile task detail drawer / landscape split.
 *   row 1: flownodename (activity name) + labels
 *   row 2: small icon + processmodelname (lighter caption)
 * Right side: action buttons (Download / Preview / Share) + close (X).
 */
const MobileDetailHeader: React.FC<MobileDetailHeaderProps> = ({
  task,
  onClose,
  onDownload,
  onPreview,
  onShare,
  disabled,
  showActions = true,
}) => {
  const translations = useTranslations();
  const labels = getLabelDataFromJsonTags(task.jsontag);

  const downloadLabel = getTranslation(translations, "generic.save") || "Download";
  const previewLabel = getTranslation(translations, "generic.print") || "Anteprima";
  const shareLabel = getTranslation(translations, "generic.share") || "Condividi";
  const closeLabel = getTranslation(translations, "generic.button.cancel") || "Chiudi";

  const activityName = task.flownodename || task.name || "";
  const modelName = task.processmodelname || "";

  return (
    <Box
      sx={{
        px: 1.5,
        py: 1,
        borderBottom: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={0.75}>
        <Stack direction="column" spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
          {/* Row 1: attività + labels */}
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 800,
                fontSize: 13,
                color: "text.primary",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {activityName}
            </Typography>
            {labels.length > 0 && (
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Labels labelCompleteMode labels={labels} />
              </Box>
            )}
          </Stack>

          {/* Row 2: processmodelname with a small icon (matches the desktop dialog) */}
          {modelName && (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
              <FlownodeIcon sx={{ fontSize: 12, color: "primary.main", flexShrink: 0 }} />
              <Typography
                variant="caption"
                sx={{
                  fontSize: 10,
                  color: "text.secondary",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {modelName}
              </Typography>
            </Stack>
          )}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
          {showActions && (
            <>
              {onPreview && (
                <Tooltip title={previewLabel} arrow>
                  <span>
                    <FlowentiButton
                      id="mobile-header-preview-btn"
                      size="small"
                      variant="outlined"
                      onClick={onPreview}
                      disabled={disabled}
                      sx={{
                        minWidth: 0,
                        px: 0.75,
                        py: 0.5,
                        borderRadius: 1.5,
                      }}
                    >
                      <VisibilityIcon sx={{ fontSize: 14 }} />
                    </FlowentiButton>
                  </span>
                </Tooltip>
              )}
              {onDownload && (
                <Tooltip title={downloadLabel} arrow>
                  <span>
                    <FlowentiButton
                      id="mobile-header-download-btn"
                      size="small"
                      variant="outlined"
                      onClick={onDownload}
                      disabled={disabled}
                      sx={{
                        minWidth: 0,
                        px: 0.75,
                        py: 0.5,
                        borderRadius: 1.5,
                      }}
                    >
                      <SaveAltIcon sx={{ fontSize: 14 }} />
                    </FlowentiButton>
                  </span>
                </Tooltip>
              )}
              {onShare && (
                <Tooltip title={shareLabel} arrow>
                  <span>
                    <FlowentiButton
                      id="mobile-header-share-btn"
                      size="small"
                      variant="outlined"
                      onClick={onShare}
                      disabled={disabled}
                      sx={{
                        minWidth: 0,
                        px: 0.75,
                        py: 0.5,
                        borderRadius: 1.5,
                      }}
                    >
                      <ShareIcon sx={{ fontSize: 14 }} />
                    </FlowentiButton>
                  </span>
                </Tooltip>
              )}
            </>
          )}
          <Tooltip title={closeLabel} arrow>
            <IconButton
              id="mobile-task-detail-close"
              size="small"
              onClick={onClose}
              aria-label="close"
              sx={{
                width: 28,
                height: 28,
                bgcolor: (t) => (t.palette.mode === "dark" ? "background.default" : "grey.100"),
                "&:hover": {
                  bgcolor: (t) => (t.palette.mode === "dark" ? "action.hover" : "grey.200"),
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Box>
  );
};

export default MobileDetailHeader;